// src/pages/MsoComparisonPage.tsx
//
// ★★ Q-54 — THE DECISION SCREEN FOR THE PRODUCT OWNER AND PSD: "WHAT WOULD THE RULE ENGINE HAVE CHARGED THE
// BILLS WE ALREADY HOLD, AND HOW FAR IS THAT FROM WHAT THEY WERE CHARGED?"
//
// Read-only. Nothing on this page prices a bill, changes one or posts to the annual-cap ledger. Every figure is the
// engine run over the MSO inputs recorded per bill (a live bill carries only an amount), so every row names where
// its inputs came from: a DEMONSTRATION row is seeded for the walkthrough and is not a fact about the bill.
//
// The page is deliberately populated on the demonstration estate, with a spread of scenarios including one the pack
// leaves undefined, so a decision can be made by looking at it.
import { CategoryBadge } from '../components/StatusBadge'
import { useEffect, useId, useState } from 'react'
import { Link } from 'react-router'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { sortParams, useSort } from '../lib/useSort'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { useOffsetPager } from '../lib/useOffsetPager'
import { formatCents, formatIsoDate } from '../lib/format'
import { BASIS_SOURCE_SHORT, describeBasis, shortChange } from '../lib/mso-comparison'
import { msoRefusalLabel } from '../lib/mso-vocabulary'
import { Money } from '../components/Money'
import { Pagination } from '../components/Pagination'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import type { MsoShadowSummary } from '../lib/types'

type ComparisonSortKey = 'bill' | 'serviceDate' | 'amount' | 'inputs' | 'flat' | 'engine' | 'rule' | 'change'
const COMPARISON_COLUMN_LABELS: Readonly<Record<ComparisonSortKey, string>> = {
  bill: 'Bill',
  serviceDate: 'Service date',
  amount: 'Amount',
  inputs: 'MSO inputs',
  flat: 'Government today',
  engine: 'Government, engine',
  rule: 'Engine rule',
  change: 'Change',
}

const PAGE_SIZE = 25

export function MsoComparisonPage() {
  const baseId = useId()
  const [differsOnly, setDifferOnly] = useState(false)
  const [pagerOffset, setPagerOffset] = useState(0)
  const { sort, onSort, interacted } = useSort<ComparisonSortKey>()
  const result = useApi(
    () => api.msoComparison({ differsOnly, limit: PAGE_SIZE, offset: pagerOffset, ...sortParams(sort) }),
    [differsOnly, pagerOffset, sort.column, sort.dir],
  )
  const rows = result.data?.comparisons ?? []
  const total = result.data?.total ?? 0
  const pager = useOffsetPager(PAGE_SIZE, total)
  useEffect(() => {
    if (pager.offset !== pagerOffset) setPagerOffset(pager.offset)
  }, [pager.offset, pagerOffset])
  useEffect(() => {
    pager.reset()
  }, [differsOnly, sort.column, sort.dir])

  const summary = result.data?.summary
  const anyDemonstration = rows.some((r) => r.basis.source === 'DEMONSTRATION')

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl mb-2">MSO engine comparison</h1>
        <p className="text-[var(--sgds-body-color-subtle)] max-w-[75ch]">
          What the rule engine would have charged each MSO bill, beside what the flat-rate path charged it. This
          page is for deciding whether the engine should price live bills. It changes no bill and posts nothing to the
          annual cap.
        </p>
        <p className="text-sm max-w-[75ch] mt-2" role="note" data-testid="basis-note">
          <span className="font-semibold">Read this first.</span> A live bill carries only an amount. The engine also needs who
          the beneficiary is and what kind of care it was, so each bill here carries recorded MSO inputs.
          {anyDemonstration
            ? ' Rows marked Demonstration use inputs seeded for the walkthrough: they are not facts about those bills.'
            : ''}{' '}
          <Link to="/mso/calculator">Try any combination in the calculator.</Link>
        </p>
      </div>

      {result.error !== null ? (
        <ErrorCard title="Could not load the comparison" message={result.error} {...(result.retryable && { onRetry: result.reload })} />
      ) : result.initialLoading || summary === undefined ? (
        <LoadingCard label="Loading the comparison" rows={4} />
      ) : summary.billsCompared === 0 ? (
        <EmptyCard
          title="No bill holds MSO inputs yet"
          message="Open an MSO bill and record its inputs (beneficiary, care setting and so on) to see what the engine would charge. On the demonstration estate these are seeded; in a real environment who supplies them is still to be decided."
        />
      ) : (
        <>
          <Summary summary={summary} />

          <div className="pt-controlbar pt-no-print" role="group" aria-label="Filter the comparison">
            <div className="flex items-center gap-2 text-sm">
              <input id={`${baseId}-differs`} type="checkbox" checked={differsOnly} onChange={(e) => setDifferOnly(e.target.checked)} />
              <label htmlFor={`${baseId}-differs`} className="font-semibold">
                Only bills whose split would change
              </label>
            </div>
          </div>

          <SortAnnouncement sort={sort} interacted={interacted} busy={result.loading} columnLabels={COMPARISON_COLUMN_LABELS} rowCount={rows.length} />

          {rows.length === 0 ? (
            <EmptyCard title="No bill matches" message="Every compared bill would be charged the same under the engine." goodNews />
          ) : (
            <div className="pt-card p-0 overflow-hidden">
              <div className="pt-table-wrap" aria-busy={result.loading}>
                <table className="pt-table">
                  <caption className="sr-only">Bills compared with the rule engine</caption>
                  <thead>
                    <tr>
                      <SortableTh label="Bill" sortKey="bill" sort={sort} onSort={onSort} />
                      <SortableTh label="Service date" sortKey="serviceDate" sort={sort} onSort={onSort} />
                      <SortableTh label="Amount" sortKey="amount" sort={sort} onSort={onSort} align="right" />
                      <SortableTh label="MSO inputs" sortKey="inputs" sort={sort} onSort={onSort} />
                      <SortableTh label="Government today" sortKey="flat" sort={sort} onSort={onSort} align="right" />
                      <SortableTh label="Government, engine" sortKey="engine" sort={sort} onSort={onSort} align="right" />
                      <SortableTh label="Engine rule" sortKey="rule" sort={sort} onSort={onSort} />
                      <SortableTh label="Change" sortKey="change" sort={sort} onSort={onSort} align="right" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((c) => (
                      <tr key={c.billReference}>
                        <td className="pt-mono whitespace-nowrap">
                          <Link to={`/bills/${encodeURIComponent(c.billReference)}`}>{c.billReference}</Link>
                        </td>
                        <td className="text-sm whitespace-nowrap">{formatIsoDate(c.serviceDate)}</td>
                        <td className="pt-num">{formatCents(c.amountCents)}</td>
                        <td className="text-sm">
                          {describeBasis(c.basis)}
                          <span className="block text-xs text-[var(--sgds-body-color-subtle)]">{BASIS_SOURCE_SHORT[c.basis.source]}</span>
                        </td>
                        <td className="pt-num">
                          <Money cents={c.flat.governmentCents} placeholder="Not computed" />
                        </td>
                        <td className="pt-num">
                          <Money cents={c.engine.governmentCents} placeholder="Refused" />
                        </td>
                        <td className="text-sm">
                          {c.engine.outcome === 'CALCULATED' ? (
                            <span className="pt-mono whitespace-nowrap">
                              {c.engine.ruleKey} v{c.engine.ruleVersion}
                            </span>
                          ) : (
                            <>
                              <CategoryBadge kind="refusal" value={c.engine.refusalKind ?? 'REFUSED'} label={msoRefusalLabel(c.engine.refusalKind ?? 'REFUSED')} />
                              {/* the column sorts by the rule key, so a refusal shows the key it sorts under */}
                              <span className="block text-xs pt-mono text-[var(--sgds-body-color-subtle)]" data-testid="refusal-rule">{c.engine.ruleKey === null ? 'no rule' : c.engine.ruleKey}</span>
                            </>
                          )}
                        </td>
                        <td className="pt-num font-semibold whitespace-nowrap">{shortChange(c.governmentChangeCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination pager={pager} label="bills" total={total} shown={rows.length} />
              <p className="px-4 pb-4 text-sm text-[var(--sgds-body-color-subtle)]">
                {rows.length} of {total} bill(s) shown, {sort.column === null ? 'largest change first' : 'in the order you chose'}. A positive change means Government would pay more.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function Summary({ summary }: { summary: MsoShadowSummary }) {
  const change = summary.governmentChangeCents
  const tile = (label: string, value: string, note?: string) => (
    <div className="pt-card flex flex-col gap-1">
      <dt className="text-sm text-[var(--sgds-body-color-subtle)]">{label}</dt>
      <dd className="m-0 text-2xl pt-tabular font-semibold">{value}</dd>
      {note !== undefined && <dd className="m-0 text-xs text-[var(--sgds-body-color-subtle)]">{note}</dd>}
    </div>
  )
  return (
    <section aria-label="Summary" className="flex flex-col gap-4">
      <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 m-0">
        {tile('Bills compared', String(summary.billsCompared), `${summary.engineCalculated} calculated, ${summary.engineRefused} refused`)}
        {tile('Government pays today', formatCents(summary.flatGovernmentCents), 'flat-rate path, computed bills only')}
        {tile('Government would pay', formatCents(summary.engineGovernmentCents), 'rule engine, same bills')}
        {tile(
          'Change',
          change === 0 ? 'No change' : `${change > 0 ? '+' : '-'}${formatCents(Math.abs(change))}`,
          `${summary.billsWhoseSplitChanges} bill(s) would be split differently`,
        )}
      </dl>
      {summary.truncated && (
        <p role="note" className="text-sm m-0">
          More bills hold MSO inputs than one comparison evaluates. The first {summary.ceiling} are included, so these totals are partial.
        </p>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="pt-card">
          <h2 className="text-base font-semibold mt-0 mb-2">By rule the engine selected</h2>
          {summary.byRule.length === 0 ? (
            <p className="text-sm m-0">No bill was priced by a rule.</p>
          ) : (
            <table className="pt-table" style={{ minWidth: 0 }}>
              <caption className="sr-only">Bills and Government change by rule</caption>
              <thead>
                <tr>
                  <th scope="col" data-unsortable="a three-row summary in rule order">Rule</th>
                  <th scope="col" className="pt-num" data-unsortable="a three-row summary in rule order">Bills</th>
                  <th scope="col" className="pt-num" data-unsortable="a three-row summary in rule order">Government change</th>
                </tr>
              </thead>
              <tbody>
                {summary.byRule.map((r) => (
                  <tr key={r.ruleKey}>
                    <td className="pt-mono">{r.ruleKey}</td>
                    <td className="pt-num">{r.bills}</td>
                    <td className="pt-num">{shortChange(r.governmentChangeCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="pt-card">
          <h2 className="text-base font-semibold mt-0 mb-2">Bills the engine refused</h2>
          {summary.byRefusal.length === 0 ? (
            <p className="text-sm m-0">None. Every compared bill fell under a rule.</p>
          ) : (
            <ul className="text-sm m-0 pl-5">
              {summary.byRefusal.map((r) => (
                <li key={r.kind}>
                  {msoRefusalLabel(r.kind)}: {r.bills} bill(s)
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs mt-3 mb-0 text-[var(--sgds-body-color-subtle)]">
            A refusal means the rules do not define that combination, for example an inpatient bill with no special
            condition. Live, such a bill would have no price at all.
          </p>
        </div>
      </div>
    </section>
  )
}
